import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Siren, Copy, Check, Loader2, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

function RoleChip({ value, current, onChange, icon: Icon, label, testId }) {
  const active = current === value;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(value)}
      className={[
        "flex-1 inline-flex items-center justify-center gap-2 rounded-full border h-11 px-4 text-sm font-semibold transition-all",
        active
          ? "bg-brand-navy text-white border-brand-navy shadow-sm"
          : "bg-white text-brand-navy border-slate-300 hover:border-brand-navy/60",
      ].join(" ")}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

export default function HeroesDialog({ trigger, testIdPrefix = "heroes" }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "active_duty",
  });

  const reset = () => {
    setResult(null);
    setCopied(false);
    setForm({ name: "", email: "", phone: "", role: "active_duty" });
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/responder-discounts", form);
      setResult(data);
      toast.success("Heroes code unlocked! 15% off your next order.");
    } catch (err) {
      toast.error("Could not generate code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild data-testid={`${testIdPrefix}-trigger`}>
        {trigger}
      </DialogTrigger>

      <DialogContent
        data-testid={`${testIdPrefix}-dialog`}
        className="sm:max-w-md rounded-3xl border-slate-200 p-0 overflow-hidden"
      >
        <div className="relative bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 px-7 pt-8 pb-7 border-b border-blue-100">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
            <BadgePercent className="h-3 w-3" /> {t("heroes.dialog.tag")}
          </span>
          <DialogHeader className="mt-3 text-left space-y-2">
            <DialogTitle className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy">
              {t("heroes.dialog.title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 leading-relaxed">
              {t("heroes.dialog.desc")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-6">
          {result ? (
            <div className="text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-4">
                <Check className="h-7 w-7" />
              </div>
              <p className="text-sm text-slate-500">{t("heroes.dialog.code")}</p>
              <div
                data-testid={`${testIdPrefix}-code`}
                className="mt-2 inline-flex items-center gap-3 rounded-2xl bg-brand-navy text-white px-5 py-3 font-display text-xl tracking-[0.2em] font-bold"
              >
                {result.code}
                <button
                  onClick={copy}
                  data-testid={`${testIdPrefix}-copy-btn`}
                  className="rounded-full bg-white/10 hover:bg-white/20 p-1.5 transition-colors"
                  aria-label="Copy code"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-300" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="mt-5 text-sm text-slate-600 leading-relaxed">
                {t("heroes.dialog.mention")}
              </p>
              <Button
                data-testid={`${testIdPrefix}-schedule-btn`}
                asChild
                className="mt-6 w-full rounded-full bg-brand-blue hover:bg-brand-navy text-white h-11"
                onClick={() => setOpen(false)}
              >
                <a href="#schedule">{t("heroes.dialog.scheduleFirst")}</a>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("heroes.dialog.role")}
                </Label>
                <div className="mt-2 flex gap-2">
                  <RoleChip
                    value="active_duty"
                    current={form.role}
                    onChange={(role) => setForm({ ...form, role })}
                    icon={Shield}
                    label={t("heroes.dialog.role.active_duty")}
                    testId={`${testIdPrefix}-role-active_duty`}
                  />
                  <RoleChip
                    value="first_responder"
                    current={form.role}
                    onChange={(role) => setForm({ ...form, role })}
                    icon={Siren}
                    label={t("heroes.dialog.role.first_responder")}
                    testId={`${testIdPrefix}-role-first_responder`}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="h-name" className="text-brand-navy font-semibold">
                  {t("heroes.dialog.fullName")}
                </Label>
                <Input
                  id="h-name"
                  data-testid={`${testIdPrefix}-input-name`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="h-email" className="text-brand-navy font-semibold">
                  {t("heroes.dialog.email")}
                </Label>
                <Input
                  id="h-email"
                  data-testid={`${testIdPrefix}-input-email`}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="h-phone" className="text-brand-navy font-semibold">
                  {t("heroes.dialog.phone")}{" "}
                  <span className="text-slate-400 font-normal">
                    {t("heroes.dialog.optional")}
                  </span>
                </Label>
                <Input
                  id="h-phone"
                  data-testid={`${testIdPrefix}-input-phone`}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 h-11"
                />
              </div>

              <Button
                type="submit"
                data-testid={`${testIdPrefix}-submit-btn`}
                disabled={loading}
                className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-11 font-semibold gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />{" "}
                    {t("heroes.dialog.submitting")}
                  </>
                ) : (
                  <>
                    <BadgePercent className="h-4 w-4" /> {t("heroes.dialog.submit")}
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                {t("heroes.dialog.disclaimer")}
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
